<template>
  <div class="gala-container">
    <PageHeader title="Gála Kezelése" subtitle="Gála események létrehozása és kezelése">
      <template #icon>
        <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"/>
          <circle cx="12" cy="12" r="10"/>
          <line x1="12" y1="8" x2="12.01" y2="12"/>
        </svg>
      </template>
    </PageHeader>

    <div class="gala-content">
      <!-- Loading State -->
      <div v-if="loading" class="loading-container">
        <div class="loading-spinner"></div>
        <p>Betöltés...</p>
      </div>

      <!-- Error State -->
      <div v-if="error && !loading" class="error-container">
        <div class="error-icon">⚠️</div>
        <p>{{ error }}</p>
        <button @click="fetchGalas" class="btn-secondary">Újrapróbálás</button>
      </div>

      <!-- Galas Management Section -->
      <div v-if="!loading && !error" class="section">
        <div class="section-header">
          <div class="header-left">
            <h2>Gála Események</h2>
            <div class="search-container">
              <div class="search-wrapper">
                <div class="search-input-container">
                  <div class="search-icon-wrapper">
                    <svg class="search-icon" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
                      <circle cx="11" cy="11" r="8"/>
                      <path d="m21 21-4.35-4.35"/>
                    </svg>
                  </div>
                  <input
                    v-model="searchQuery"
                    type="text"
                    placeholder="Keresés galák között... (név, helyszín, dátum)"
                    class="search-input"
                    @focus="searchFocused = true"
                    @blur="onSearchBlur"
                  />
                  <div v-if="searchQuery" class="search-results-mini">
                    {{ filteredGalas.length }} találat
                  </div>
                  <button
                    v-if="searchQuery"
                    @click="clearSearch"
                    class="clear-search-btn"
                    title="Keresés törlése"
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                      <line x1="18" y1="6" x2="6" y2="18"/>
                      <line x1="6" y1="6" x2="18" y2="18"/>
                    </svg>
                  </button>
                </div>
                
                <!-- Search Dropdown -->
                <div v-if="searchFocused && searchQuery" class="search-dropdown">
                  <div v-if="filteredGalas.length === 0" class="search-no-results">
                    <div class="no-results-icon">
                      <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
                        <circle cx="11" cy="11" r="8"/>
                        <path d="m21 21-4.35-4.35"/>
                        <line x1="11" y1="8" x2="11" y2="12"/>
                        <line x1="11" y1="16" x2="11.01" y2="16"/>
                      </svg>
                    </div>
                    <div class="no-results-content">
                      <p class="no-results-title">Nincs találat</p>
                      <p class="no-results-text">Próbáljon más kulcsszavakat használni</p>
                    </div>
                  </div>
                  <div v-else class="search-results-list">
                    <div class="search-results-header">
                      <div class="results-info">
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                          <path d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"/>
                        </svg>
                        <span class="results-title">{{ filteredGalas.length }} találat</span>
                      </div>
                      <div class="results-actions">
                        <span v-if="filteredGalas.length > 6" class="more-results-badge">
                          +{{ filteredGalas.length - 6 }} további
                        </span>
                      </div>
                    </div>
                    <div class="search-results-items">
                      <div
                        v-for="gala in filteredGalas.slice(0, 6)"
                        :key="gala.id"
                        class="search-result-item"
                        @click="scrollToGala(gala.id)"
                      >
                        <div class="result-main">
                          <div class="result-icon">
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                              <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"/>
                              <circle cx="12" cy="12" r="10"/>
                            </svg>
                          </div>
                          <div class="result-content">
                            <div class="result-name">{{ gala.name }}</div>
                            <div class="result-location">
                              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                                <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z"/>
                                <circle cx="12" cy="10" r="3"/>
                              </svg>
                              {{ gala.location }}
                            </div>
                          </div>
                        </div>
                        <div class="result-meta">
                          <span class="result-date">
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                              <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/>
                              <line x1="16" y1="2" x2="16" y2="6"/>
                              <line x1="8" y1="2" x2="8" y2="6"/>
                              <line x1="3" y1="10" x2="21" y2="10"/>
                            </svg>
                            {{ formatDate(gala.event_date) }}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <button @click="showCreateModal = true" class="btn-primary create-btn">
            <div class="btn-icon">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <line x1="12" y1="5" x2="12" y2="19"/>
                <line x1="5" y1="12" x2="19" y2="12"/>
              </svg>
            </div>
            <span>Új Gala</span>
          </button>
        </div>

        <!-- Empty State -->
        <div v-if="galas.length === 0" class="empty-state">
          <div class="empty-icon">🎭</div>
          <h3>Még nincs gála esemény</h3>
          <p>Hozza létre az első gála eseményt!</p>
          <button @click="showCreateModal = true" class="btn-primary">Első Gala Létrehozása</button>
        </div>

        <!-- Galas Grid -->
        <div v-else class="galas-grid">
          <GalaCard
            v-for="gala in filteredGalas"
            :key="gala.id"
            :gala="gala"
            :show-price="true"
            :ticket-stats="ticketStats"
            :show-ticket-info="true"
          >
            <template v-slot:card-actions>
              <div class="gala-actions">
                <button @click="editGala(gala)" class="btn-edit">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
                    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
                  </svg>
                  Szerkesztés
                </button>
                <button @click="deleteGala(gala.id)" class="btn-delete">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <polyline points="3,6 5,6 21,6"/>
                    <path d="M19,6v14a2,2 0 0,1 -2,2H7a2,2 0 0,1 -2,-2V6m3,0V4a2,2 0 0,1 2,-2h4a2,2 0 0,1 2,2v2"/>
                  </svg>
                  Törlés
                </button>
                <button @click="openAllocation(gala)" class="btn-allocate">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <path d="M3 7h18M3 12h18M3 17h18"/>
                  </svg>
                  Jegykiosztás
                </button>
              </div>
            </template>
          </GalaCard>
        </div>
      </div>
    </div>

    <!-- Create/Edit Modal -->
    <div v-if="showCreateModal || showEditModal" class="modal-overlay" @click="closeModal">
      <div class="modal-content" @click.stop>
        <div class="modal-header">
          <h3>{{ showCreateModal ? 'Új Gala Létrehozása' : 'Gala Szerkesztése' }}</h3>
          <button @click="closeModal" class="close-btn">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <line x1="18" y1="6" x2="6" y2="18"/>
              <line x1="6" y1="6" x2="18" y2="18"/>
            </svg>
          </button>
        </div>
        <div class="modal-body">
          <form @submit.prevent="saveGala" class="gala-form">
            <div class="form-group">
              <label for="galaName">Gala neve *</label>
              <input 
                id="galaName" 
                v-model="formData.name" 
                type="text" 
                required 
                placeholder="pl. Tavaszi Gála 2024"
                class="modern-input"
              />
            </div>
            
            <div class="form-group">
              <label for="galaDate">Esemény dátuma *</label>
              <input 
                id="galaDate" 
                v-model="formData.event_date" 
                type="date" 
                required 
                class="modern-input"
              />
            </div>
            
            <div class="form-group">
              <label for="galaLocation">Helyszín *</label>
              <input 
                id="galaLocation" 
                v-model="formData.location" 
                type="text" 
                required 
                placeholder="pl. Művelődési Ház"
                class="modern-input"
              />
            </div>
            
            <div class="form-group">
              <label for="galaDescription">Leírás</label>
              <textarea 
                id="galaDescription" 
                v-model="formData.description" 
                rows="4"
                placeholder="Részletes leírás a galáról..."
                class="modern-input"
              ></textarea>
            </div>
            
            <div class="form-group">
              <label for="galaTicketPrice">Jegyár (Ft)</label>
              <input
                id="galaTicketPrice"
                v-model="formData.ticket_price"
                type="number"
                min="0"
                step="100"
                placeholder="pl. 5000"
                class="modern-input"
              />
              <small class="form-help">0 = ingyenes esemény</small>
            </div>
            
            <div class="form-group">
              <label for="galaMaxTickets">Maximum jegyek száma</label>
              <input
                id="galaMaxTickets"
                v-model="formData.max_tickets"
                type="number"
                min="0"
                step="1"
                placeholder="pl. 100"
                class="modern-input"
              />
              <small class="form-help">0 = korlátlan jegyek</small>
            </div>
            
            <div class="form-group">
              <label for="galaDeadline">Regisztrációs határidő</label>
              <input
                id="galaDeadline"
                v-model="formData.registration_deadline"
                type="date"
                class="modern-input"
              />
              <small class="form-help">Opcionális - a határidő után nem lehet jegyet vásárolni</small>
            </div>
            
            <div class="form-group">
              <label>Fotó</label>
              <div class="photo-upload">
                <label for="galaPhoto" class="file-label">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <rect x="3" y="3" width="18" height="18" rx="2" ry="2"/>
                    <circle cx="8.5" cy="8.5" r="1.5"/>
                    <polyline points="21,15 16,10 5,21"/>
                  </svg>
                  <span>{{ photoPreview ? 'Fotó kiválasztva - kattints a cseréhez' : 'Kattints a fotó feltöltéshez' }}</span>
                </label>
                <input 
                  id="galaPhoto" 
                  type="file" 
                  @change="handlePhotoUpload" 
                  accept="image/*" 
                  class="file-input"
                />
                <div v-if="photoPreview" class="photo-preview">
                  <img :src="photoPreview" alt="Előnézet" />
                  <button type="button" @click="removePhoto" class="remove-photo">✕</button>
                </div>
              </div>
            </div>
            
            <div class="form-actions">
              <button type="button" @click="closeModal" class="btn-secondary">Mégse</button>
              <button type="submit" class="btn-primary" :disabled="saving">
                {{ saving ? 'Mentés...' : 'Mentés' }}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>

    <!-- Group Ticket Allocation Modal (Admin) -->
    <div v-if="showAllocationModal" class="modal-overlay" @click="closeAllocation">
      <div class="modal-content" @click.stop>
        <div class="modal-header">
          <h3>Jegykiosztás csoportonként – {{ allocationGala ? allocationGala.name : '' }}</h3>
          <button @click="closeAllocation" class="close-btn">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <line x1="18" y1="6" x2="6" y2="18"/>
              <line x1="6" y1="6" x2="18" y2="18"/>
            </svg>
          </button>
        </div>
        <div class="modal-body">
          <div class="allocation-summary" v-if="allocation">
            <div class="alloc-stat">
              <span class="alloc-label">Gála maximum:</span>
              <strong>{{ allocation.max_tickets }}</strong>
            </div>
            <div class="alloc-stat">
              <span class="alloc-label">Kiosztva:</span>
              <strong :class="{ over: allocation.allocated_sum > allocation.max_tickets }">
                {{ allocation.allocated_sum }}
              </strong>
            </div>
            <div class="alloc-stat">
              <span class="alloc-label">Szabad:</span>
              <strong>{{ freeTotal }}</strong>
            </div>
          </div>

          <div v-if="groups.length === 0" class="empty-hint">
            Nincsenek elérhető csoportok az adatbázisban.
          </div>

          <div v-else class="group-alloc-list">
            <div v-for="grp in groups" :key="grp.id" class="group-alloc-row">
              <span class="group-name">{{ grp.name }}</span>
              <input
                type="number"
                min="0"
                class="modern-input alloc-input"
                v-model.number="allocationInputs[grp.id]"
              />
              <span class="alloc-remaining" v-if="currentAllocation[grp.id] !== undefined">
                (szabad: {{ remainingFor(grp.id) }})
              </span>
            </div>
          </div>

          <p v-if="overAllocated" class="alloc-warning">
            A kiosztott jegyek összesen meghaladja a gála maximumát!
          </p>

          <div class="form-actions">
            <button type="button" @click="closeAllocation" class="btn-secondary">Mégse</button>
            <button type="button" @click="saveAllocation" class="btn-primary" :disabled="savingAllocation || overAllocated">
              {{ savingAllocation ? 'Mentés...' : 'Kiosztás mentése' }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import toastMixin from '../mixins/toast.js';
import GalaCard from './shared/GalaCard.vue';
import PageHeader from './shared/PageHeader.vue';

export default {
  name: 'GalaItem',
  components: {
    GalaCard,
    PageHeader
  },
  mixins: [toastMixin],
  
  data() {
    return {
      galas: [],
      loading: false,
      error: null,
      saving: false,
      showCreateModal: false,
      showEditModal: false,
      selectedGala: null,
      photoPreview: null,
      ticketStats: {},
      searchQuery: '',
      searchFocused: false,
      formData: {
        name: '',
        event_date: '',
        location: '',
        description: '',
        ticket_price: 0,
        max_tickets: 0,
        registration_deadline: '',
        photo: null
      },
      showAllocationModal: false,
      allocationGala: null,
      allocation: null,
      groups: [],
      allocationInputs: {},
      currentAllocation: {},
      savingAllocation: false
    };
  },
  
  computed: {
    isAdmin() {
      return this.$store.getters.isAdmin;
    },
    isAssistant() {
      return this.$store.getters.isAssistant;
    },
    freeTotal() {
      if (!this.allocation) return 0;
      return this.allocation.max_tickets - this.allocation.allocated_sum;
    },

    allocatedInputSum() {
      return Object.values(this.allocationInputs).reduce((a, b) => a + (Number(b) || 0), 0);
    },

    overAllocated() {
      if (!this.allocation) return false;
      const max = this.allocation.max_tickets;
      if (max === 0) return false;
      return this.allocatedInputSum > max;
    },
    
    filteredGalas() {
      if (!this.searchQuery.trim()) {
        return this.galas;
      }
      
      const query = this.searchQuery.toLowerCase().trim();
      return this.galas.filter(gala => {
        return (
          gala.name.toLowerCase().includes(query) ||
          gala.location.toLowerCase().includes(query) ||
          gala.description.toLowerCase().includes(query) ||
          gala.event_date.includes(query) ||
          (gala.registration_deadline && gala.registration_deadline.includes(query))
        );
      });
    }
  },
  
  async mounted() {
    if (!this.isAdmin && !this.isAssistant) {
      this.showToast('Nincs jogosultsága a gala kezeléshez!', 'error');
      this.$router.push('/');
      return;
    }
    await this.fetchGalas();
  },
  
  methods: {
    async fetchGalas() {
      this.loading = true;
      this.error = null;
      
      try {
        const response = await axios.get(`${this.$BACKEND_HOST}/api/galas`);
        this.galas = response.data.galas || [];
        
        // Fetch ticket stats for all galas to show availability
        await this.fetchTicketStats();
      } catch (error) {
        console.error('Error fetching galas:', error);
        this.error = 'Hiba történt a galák betöltésekor.';
        this.showToast('Hiba történt a galák betöltésekor!', 'error');
      } finally {
        this.loading = false;
      }
    },
    
    async fetchTicketStats() {
      try {
        // Fetch stats for all galas to show ticket availability
        for (const gala of this.galas) {
          try {
            const response = await axios.get(`${this.$BACKEND_HOST}/api/gala/${gala.id}/stats`);
            const stats = response.data.stats;
            if (stats) {
              this.ticketStats[gala.id] = {
                sold_tickets: stats.yes_count || 0, // Count of "yes" responses
                total_tickets: stats.total_tickets || gala.max_tickets || 0
              };
            }
          } catch (error) {
            console.error(`Error fetching stats for gala ${gala.id}:`, error);
            // If stats can't be fetched, assume 0 sold tickets
            this.ticketStats[gala.id] = {
              sold_tickets: 0,
              total_tickets: gala.max_tickets || 0
            };
          }
        }
      } catch (error) {
        console.error('Error fetching ticket stats:', error);
      }
    },
    
    async saveGala() {
      this.saving = true;
      
      try {
        const formData = new FormData();
        formData.append('name', this.formData.name);
        formData.append('event_date', this.formData.event_date);
        formData.append('location', this.formData.location);
        formData.append('description', this.formData.description);
        formData.append('ticket_price', this.formData.ticket_price || 0);
        formData.append('max_tickets', this.formData.max_tickets || 0);
        formData.append('registration_deadline', this.formData.registration_deadline || '');
        
        if (this.formData.photo) {
          formData.append('photo', this.formData.photo);
        }
        
        let response;
        if (this.showCreateModal) {
          response = await axios.post(`${this.$BACKEND_HOST}/api/gala`, formData, {
            headers: {
              'Content-Type': 'multipart/form-data'
            }
          });
          this.showToast('Gala sikeresen létrehozva!', 'success');
        } else {
          response = await axios.put(`${this.$BACKEND_HOST}/api/gala/${this.selectedGala.id}`, formData, {
            headers: {
              'Content-Type': 'multipart/form-data'
            }
          });
          this.showToast('Gala sikeresen frissítve!', 'success');
        }
        
        await this.fetchGalas(); // This will also refresh ticket stats
        this.closeModal();
      } catch (error) {
        console.error('Error saving gala:', error);
        let errorMessage = 'Hiba történt a gala mentésekor!';
        if (error.response && error.response.data && error.response.data.message) {
          errorMessage = error.response.data.message;
        } else if (error.message) {
          errorMessage = error.message;
        }
        this.showToast(errorMessage, 'error');
      } finally {
        this.saving = false;
      }
    },
    
    async deleteGala(galaId) {
      if (!confirm('Biztosan törölni szeretné ezt a galát?')) {
        return;
      }
      
      try {
        await axios.delete(`${this.$BACKEND_HOST}/api/gala/${galaId}`);
        this.showToast('Gala sikeresen törölve!', 'success');
        await this.fetchGalas(); // This will also refresh ticket stats
      } catch (error) {
        console.error('Error deleting gala:', error);
        this.showToast('Hiba történt a gala törlésekor!', 'error');
      }
    },
    
    async openAllocation(gala) {
      this.allocationGala = gala;
      this.allocationInputs = {};
      this.currentAllocation = {};
      this.showAllocationModal = true;
      try {
        const [groupsRes, allocRes] = await Promise.all([
          axios.get(`${this.$BACKEND_HOST}/api/settings/groups/info`),
          axios.get(`${this.$BACKEND_HOST}/api/gala/${gala.id}/group-tickets`)
        ]);
        this.groups = groupsRes.data.groups || [];
        this.allocation = allocRes.data.allocation || { gala_id: gala.id, max_tickets: gala.max_tickets || 0, allocated_sum: 0, groups: [] };
        const existing = {};
        (this.allocation.groups || []).forEach(g => {
          existing[g.group_id] = g.quota;
          this.allocationInputs[g.group_id] = g.quota;
        });
        this.currentAllocation = existing;
        // Default unallocated groups to 0.
        this.groups.forEach(g => {
          if (this.allocationInputs[g.id] === undefined) {
            this.allocationInputs[g.id] = 0;
          }
        });
      } catch (error) {
        console.error('Error loading allocation:', error);
        this.showToast('Hiba a jegykiosztás betöltésekor!', 'error');
        this.showAllocationModal = false;
      }
    },

    closeAllocation() {
      this.showAllocationModal = false;
      this.allocationGala = null;
      this.allocation = null;
      this.groups = [];
      this.allocationInputs = {};
      this.currentAllocation = {};
    },

    remainingFor(groupId) {
      const quota = this.currentAllocation[groupId] || 0;
      const committed = (this.allocation && this.allocation.groups
        ? this.allocation.groups.find(g => g.group_id === groupId)
        : null);
      const used = committed ? committed.committed : 0;
      return quota - used;
    },

    async saveAllocation() {
      if (!this.allocationGala) return;
      this.savingAllocation = true;
      try {
        const quotas = {};
        Object.keys(this.allocationInputs).forEach(k => {
          quotas[k] = Number(this.allocationInputs[k]) || 0;
        });
        await axios.post(`${this.$BACKEND_HOST}/api/gala/group-tickets`, {
          gala_id: this.allocationGala.id,
          quotas
        });
        this.showToast('Jegykiosztás mentve!', 'success');
        this.closeAllocation();
        await this.fetchGalas();
      } catch (error) {
        console.error('Error saving allocation:', error);
        let msg = 'Hiba a jegykiosztás mentésekor!';
        if (error.response && error.response.data && error.response.data.message) {
          msg = error.response.data.message;
        }
        this.showToast(msg, 'error');
      } finally {
        this.savingAllocation = false;
      }
    },

    editGala(gala) {
      this.selectedGala = gala;
      this.formData = {
        name: gala.name,
        event_date: gala.event_date,
        location: gala.location,
        description: gala.description,
        ticket_price: gala.ticket_price || 0,
        max_tickets: gala.max_tickets || 0,
        registration_deadline: gala.registration_deadline || '',
        photo: null
      };
      
      // Load existing photo preview if exists
      if (gala.photo_hash) {
        this.photoPreview = `${this.$BACKEND_HOST}/api/gala/photo/${gala.photo_hash}`;
      } else {
        this.photoPreview = null;
      }
      
      this.showEditModal = true;
    },
    
    closeModal() {
      this.showCreateModal = false;
      this.showEditModal = false;
      this.selectedGala = null;
      this.photoPreview = null;
      this.formData = {
        name: '',
        event_date: '',
        location: '',
        description: '',
        ticket_price: 0,
        max_tickets: 0,
        registration_deadline: '',
        photo: null
      };
    },
    
    handlePhotoUpload(event) {
      const file = event.target.files[0];
      if (file) {
        this.formData.photo = file;
        
        // Create preview
        const reader = new FileReader();
        reader.onload = (e) => {
          this.photoPreview = e.target.result;
        };
        reader.readAsDataURL(file);
      }
    },
    
    removePhoto() {
      this.formData.photo = null;
      this.photoPreview = null;
      
      // Clear file input
      const fileInput = document.getElementById('galaPhoto');
      if (fileInput) {
        fileInput.value = '';
      }
    },
    
    clearSearch() {
      this.searchQuery = '';
    },

    onSearchBlur() {
      // Add small delay to allow click events on search results
      setTimeout(() => {
        this.searchFocused = false;
      }, 200);
    },

    scrollToGala(galaId) {
      const element = document.querySelector(`[data-gala-id="${galaId}"]`);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'center' });
        this.searchFocused = false;
      }
    },

    formatDate(dateString) {
      if (!dateString) return '';
      const date = new Date(dateString);
      return date.toLocaleDateString('hu-HU');
    }
  }
};
</script>

<style scoped>
.gala-container {
  padding: 2rem;
  max-width: 1200px;
  margin: 0 auto;
  min-height: calc(100vh - 4rem);
}

.section {
  background: var(--card-bg);
  border-radius: 16px;
  box-shadow: var(--shadow);
  border: 1px solid var(--color-border);
  margin-bottom: 2rem;
  overflow: hidden;
}

.section-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  padding: 1.5rem 2rem;
  background: var(--color-background-mute);
  border-bottom: 1px solid var(--color-border);
  gap: 2rem;
}

.header-left {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.section-header h2 {
  color: var(--color-heading);
  font-size: 1.5rem;
  font-weight: 600;
  margin: 0;
}

/* Enhanced Search Styles */
.search-container {
  position: relative;
  width: 100%;
  max-width: 480px;
}

.search-wrapper {
  position: relative;
  width: 100%;
}

.search-input-container {
  position: relative;
  display: flex;
  align-items: center;
  background: var(--color-background);
  border: 2px solid var(--color-border);
  border-radius: 12px;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  overflow: hidden;
}

.search-input-container:focus-within {
  border-color: var(--primary-blue);
  box-shadow: 0 0 0 4px rgba(59, 130, 246, 0.1);
  transform: translateY(-1px);
}

.search-icon-wrapper {
  position: absolute;
  left: 1rem;
  z-index: 2;
  color: var(--color-text-muted);
  transition: color 0.3s ease;
  pointer-events: none;
}

.search-input-container:focus-within .search-icon-wrapper {
  color: var(--primary-blue);
}

.search-input {
  width: 100%;
  padding: 1rem 1rem 1rem 3rem;
  border: none;
  background: transparent;
  color: var(--color-text);
  font-size: 1rem;
  font-weight: 500;
  outline: none;
}

.search-input::placeholder {
  color: var(--color-text-muted);
  font-weight: 400;
}

.search-results-mini {
  position: absolute;
  right: 3.5rem;
  background: var(--primary-blue);
  color: white;
  padding: 0.25rem 0.75rem;
  border-radius: 20px;
  font-size: 0.875rem;
  font-weight: 600;
  white-space: nowrap;
}

.clear-search-btn {
  position: absolute;
  right: 1rem;
  background: none;
  border: none;
  color: var(--color-text-muted);
  cursor: pointer;
  padding: 0.5rem;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
  width: 32px;
  height: 32px;
}

.clear-search-btn:hover {
  background: var(--error);
  color: white;
  transform: scale(1.1);
}

/* Search Dropdown */
.search-dropdown {
  position: absolute;
  top: calc(100% + 0.5rem);
  left: 0;
  right: 0;
  background: var(--card-bg);
  border: 1px solid var(--color-border);
  border-radius: 12px;
  box-shadow: var(--shadow-xl);
  z-index: 1000;
  backdrop-filter: blur(20px);
  animation: searchDropdownSlide 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

@keyframes searchDropdownSlide {
  from {
    opacity: 0;
    transform: translateY(-10px) scale(0.95);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

.search-no-results {
  padding: 2rem;
  text-align: center;
}

.no-results-icon {
  margin-bottom: 1rem;
  opacity: 0.6;
}

.no-results-content {
  color: var(--color-text-muted);
}

.no-results-title {
  font-size: 1.125rem;
  font-weight: 600;
  color: var(--color-text);
  margin-bottom: 0.5rem;
}

.no-results-text {
  font-size: 0.875rem;
  margin: 0;
}

.search-results-list {
  max-height: 400px;
  overflow: hidden;
}

.search-results-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem 1.5rem;
  background: var(--color-background-mute);
  border-bottom: 1px solid var(--color-border);
}

.results-info {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: var(--primary-blue);
  font-weight: 600;
}

.results-title {
  font-size: 0.95rem;
}

.more-results-badge {
  background: var(--primary-blue);
  color: white;
  padding: 0.25rem 0.75rem;
  border-radius: 12px;
  font-size: 0.75rem;
  font-weight: 600;
}

.search-results-items {
  padding: 0.5rem;
  max-height: 300px;
  overflow-y: auto;
}

.search-result-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1rem;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s ease;
  border: 1px solid transparent;
  margin-bottom: 0.25rem;
}

.search-result-item:last-child {
  margin-bottom: 0;
}

.search-result-item:hover {
  background: var(--color-background-mute);
  border-color: var(--primary-blue);
  transform: translateX(4px);
}

.result-main {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  flex: 1;
}

.result-icon {
  flex-shrink: 0;
  width: 32px;
  height: 32px;
  background: linear-gradient(135deg, var(--primary-blue), var(--secondary-blue));
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
}

.result-content {
  flex: 1;
  min-width: 0;
}

.result-name {
  font-weight: 600;
  color: var(--color-text);
  font-size: 0.95rem;
  margin-bottom: 0.25rem;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.result-location {
  display: flex;
  align-items: center;
  gap: 0.375rem;
  color: var(--color-text-muted);
  font-size: 0.875rem;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.result-meta {
  flex-shrink: 0;
  margin-left: 1rem;
}

.result-date {
  display: flex;
  align-items: center;
  gap: 0.375rem;
  background: var(--color-background);
  color: var(--color-text-muted);
  padding: 0.5rem 0.75rem;
  border-radius: 6px;
  font-size: 0.8125rem;
  white-space: nowrap;
  border: 1px solid var(--color-border);
}

/* Loading and Error States */
.loading-container, .error-container {
  text-align: center;
  padding: 4rem 2rem;
  color: var(--color-text-muted);
}

.loading-spinner {
  width: 40px;
  height: 40px;
  border: 4px solid var(--color-border);
  border-top: 4px solid var(--primary-blue);
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin: 0 auto 1rem;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.error-icon {
  font-size: 3rem;
  margin-bottom: 1rem;
}

.error-container button {
  margin-top: 1rem;
}

/* Empty State */
.empty-state {
  text-align: center;
  padding: 4rem 2rem;
  color: var(--color-text-muted);
}

.empty-icon {
  font-size: 4rem;
  margin-bottom: 1rem;
}

.empty-state h3 {
  color: var(--color-heading);
  margin-bottom: 0.5rem;
}

.empty-state p {
  margin-bottom: 2rem;
}

/* Galas Grid */
.galas-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
  gap: 1.5rem;
  padding: 2rem;
}

.gala-actions {
  display: flex;
  gap: 0.75rem;
  justify-content: flex-end;
}

/* Responsive Design */
@media (max-width: 768px) {
  .gala-container {
    padding: 1rem;
  }
  
  .galas-grid {
    grid-template-columns: 1fr;
    padding: 1rem;
    gap: 1rem;
  }
  
  .section-header {
    flex-direction: column;
    gap: 1rem;
    align-items: stretch;
  }
  
  .gala-actions {
    justify-content: center;
  }
  
  .search-container {
    max-width: 100%;
  }

  .search-dropdown {
    position: fixed;
    top: auto;
    bottom: 1rem;
    left: 1rem;
    right: 1rem;
    max-height: 50vh;
  }

  .search-results-items {
    max-height: 200px;
  }
}

@media (max-width: 480px) {
  .gala-container {
    padding: 0.5rem;
  }
  
  .section {
    border-radius: 12px;
  }
  
  .search-input {
    padding: 0.875rem 0.875rem 0.875rem 2.5rem;
  }

  .search-result-item {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.5rem;
  }

  .result-meta {
    margin-left: 0;
    align-self: stretch;
  }
}

/* Modal Styles */
.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 1rem;
  backdrop-filter: blur(4px);
}

.modal-content {
  background: var(--card-bg);
  border-radius: 16px;
  box-shadow: var(--shadow-lg);
  max-width: 640px;
  width: 100%;
  max-height: 90vh;
  overflow-y: auto;
  border: 1px solid var(--color-border);
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.5rem 2rem;
  border-bottom: 1px solid var(--color-border);
  background: var(--color-background-soft);
}

.modal-header h3 {
  margin: 0;
  font-size: 1.25rem;
  font-weight: 600;
  color: var(--color-heading);
}

.close-btn {
  background: none;
  border: none;
  font-size: 1.5rem;
  cursor: pointer;
  color: var(--color-text-muted);
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  transition: all 0.2s ease;
}

.close-btn:hover {
  background: var(--color-background);
  color: var(--color-text);
}

.modal-body {
  padding: 2rem;
}

.gala-form {
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.form-group label {
  font-weight: 600;
  color: var(--color-heading);
  font-size: 0.95rem;
}

.form-help {
  color: var(--color-text-muted);
  font-size: 0.8rem;
  margin-top: 0.25rem;
}

.form-actions {
  display: flex;
  gap: 0.75rem;
  justify-content: flex-end;
  margin-top: 0.5rem;
  padding-top: 1rem;
  border-top: 1px solid var(--color-border);
}

.btn-secondary {
  background: var(--color-background);
  color: var(--color-text);
  border: 1px solid var(--color-border);
  padding: 0.6rem 1.2rem;
  border-radius: 8px;
  cursor: pointer;
  font-weight: 500;
  transition: all 0.2s ease;
}

.btn-secondary:hover {
  background: var(--color-background-mute);
  border-color: var(--color-text-muted);
}

/* Photo Upload */
.photo-upload {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  position: relative;
}

.file-label {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.75rem;
  padding: 1rem 1.25rem;
  border: 2px dashed var(--color-border);
  border-radius: 10px;
  background: var(--color-background);
  color: var(--color-text-muted);
  font-size: 0.95rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
}

.file-label:hover {
  border-color: var(--primary-blue);
  color: var(--primary-blue);
  background: var(--card-bg);
}

.file-label svg {
  flex-shrink: 0;
  opacity: 0.7;
}

.file-input {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  opacity: 0;
  cursor: pointer;
  z-index: 2;
}

.photo-preview {
  position: relative;
  display: inline-block;
  border-radius: 12px;
  overflow: hidden;
  border: 1px solid var(--color-border);
}

.photo-preview img {
  max-width: 200px;
  max-height: 200px;
  object-fit: cover;
  display: block;
}

.remove-photo {
  position: absolute;
  top: 0.5rem;
  right: 0.5rem;
  background: rgba(0, 0, 0, 0.7);
  color: white;
  border: none;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  cursor: pointer;
  font-size: 1rem;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
}

.remove-photo:hover {
  background: var(--error);
  transform: scale(1.1);
}

/* Gala page buttons */
.create-btn {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.6rem 1.2rem;
  font-weight: 500;
}

.btn-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 18px;
  height: 18px;
}

.btn-edit {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  padding: 0.45rem 0.9rem;
  background: var(--color-background);
  color: var(--color-text);
  border: 1px solid var(--color-border);
  border-radius: 8px;
  cursor: pointer;
  font-weight: 500;
  font-size: 0.875rem;
  transition: all 0.2s ease;
}

.btn-edit:hover {
  background: var(--primary-blue);
  color: white;
  border-color: var(--primary-blue);
}

.btn-delete {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  padding: 0.45rem 0.9rem;
  background: var(--color-background);
  color: var(--error);
  border: 1px solid var(--color-border);
  border-radius: 8px;
  cursor: pointer;
  font-weight: 500;
  font-size: 0.875rem;
  transition: all 0.2s ease;
}

.btn-delete:hover {
  background: var(--error);
  color: white;
  border-color: var(--error);
}

.btn-allocate {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  padding: 0.45rem 0.9rem;
  background: var(--color-background);
  color: var(--primary-blue);
  border: 1px solid var(--color-border);
  border-radius: 8px;
  cursor: pointer;
  font-weight: 500;
  font-size: 0.875rem;
  transition: all 0.2s ease;
}

.btn-allocate:hover {
  background: var(--primary-blue);
  color: white;
  border-color: var(--primary-blue);
}

/* Group ticket allocation modal */
.allocation-summary {
  display: flex;
  gap: 1.5rem;
  flex-wrap: wrap;
  margin-bottom: 1.5rem;
  padding: 1rem;
  background: var(--color-background-mute);
  border-radius: 10px;
}

.alloc-stat {
  display: flex;
  gap: 0.4rem;
  align-items: center;
}

.alloc-label {
  color: var(--color-text-muted);
  font-size: 0.9rem;
}

.alloc-stat strong.over {
  color: var(--error);
}

.group-alloc-list {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  margin-bottom: 1rem;
}

.group-alloc-row {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.group-name {
  flex: 1;
  font-weight: 500;
  color: var(--color-heading);
}

.alloc-input {
  width: 100px;
  text-align: center;
}

.alloc-remaining {
  color: var(--color-text-muted);
  font-size: 0.85rem;
  white-space: nowrap;
}

.empty-hint {
  color: var(--color-text-muted);
  padding: 1rem 0;
}

.alloc-warning {
  color: var(--error);
  font-weight: 600;
  margin-bottom: 1rem;
}

</style>
